import React, { useContext, useEffect, useState } from 'react';
import { Drawer, List, ListItem, ListItemText, IconButton, Typography, Button, Grid, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import CartContext, { useCart } from '../../contexts/CartContext';
import { CartItem, CartItemType } from '../../types';
import { safeParseFloat, formatCurrencyDisplay } from '../../utils';
import { useNavigate } from 'react-router-dom';
import removeItemFromCart from '../../hooks/cart/removeItemFromCart';
import { storeReturnToForCart } from '../../utils/navigationReturnTo';

interface CartDrawerProps {
    open: boolean;
    onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
    const { fetchCart } = useCart();
    const { cartItems } = useContext(CartContext) as unknown as { cartItems: CartItemType };
    const [items, setItems] = useState<CartItemType>(cartItems);
    const [removingItemId, setRemovingItemId] = useState<number | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        setItems(cartItems);
    }, [cartItems]);

    const handleRemoveItem = async (cartItemId: number) => {
        setRemovingItemId(cartItemId);
        try {
            const success = await removeItemFromCart(cartItemId);
            if (success) {
                await fetchCart();
            }
        } catch (error) {
            console.error('Failed to remove item from cart', error);
        } finally {
            setRemovingItemId(null);
        }
    };

    const calculateGrandTotal = () => {
        return items?.data?.reduce((total, item) => total + safeParseFloat(item.item.chargePerPeriod), 0) || 0;
    };

    const total = calculateGrandTotal();

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <div className="cart-drawer cart">
                <IconButton onClick={onClose} className="close-btn">
                    <CloseIcon />
                </IconButton>
                <Typography variant="h6" className="pb-5 pt-10">
                    <p className="cart-header">Your Cart</p>
                </Typography>
                <List className="list-block">
                    {items && items.data && items.data.length > 0 && items.data.map((item: CartItem) => (
                        <ListItem key={item.id} className={removingItemId === item.id ? 'removing mb-5' : 'mb-5'}>
                            <ListItemText className="list-item-p" primary={`${item.item.event.name}`} secondary={`${item.child?.firstName} ${item.child?.lastName}`} />
                            <Typography variant="body2">${item.item.chargePerPeriod}</Typography>
                            <Tooltip title="Remove item from cart" className="pt-0 btn-cart" onClick={() => handleRemoveItem(item.id)}>
                                <IconButton className="ptf-0 ptb-0">
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                            {removingItemId === item.id && <Typography variant="body2" className="removed-text">Item removed</Typography>}
                        </ListItem>
                    ))}
                </List>
                <div className="cart-footer pt-10">
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="subtitle1">
                                Subtotal ({items?.data?.length} items)
                            </Typography>
                        </Grid>
                        <Grid item xs={6} style={{ textAlign: 'right' }}>
                            <Typography variant="subtitle1">${formatCurrencyDisplay(total)}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Button className="btn blue" variant="contained" color="primary" onClick={() => { storeReturnToForCart(); navigate('/cart'); }}>
                                Checkout
                            </Button>
                        </Grid>
                    </Grid>
                </div>
            </div>
        </Drawer>
    );
};

export default CartDrawer;
